import React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import useFetchSchedule from "@/hooks/useFetchSchedule";
import {
    updateExistAppointment,
    updateNewAppointment,
} from "@/store/appointments/appointmentsSlice";
import { toggleVisibilityCalendarInScheduleHeader } from "@/store/ui/ui";
import ScheduleTablePopover from "../ScheduleTablePopover/ScheduleTablePopover";
import { RootState } from "@/store/store";
import { setPatientId } from "@/store/patients/patients";
import { setAppointemtId } from "@/store/schedule/scheduleSlice";
import generateTimeSlotsFunc from "@/utils/generateTimeSlots";
import calculateEndTime from "@/utils/timeCalc";
import { normalizeTime } from "@/utils/normalizeTime";
import useGetRole from "@/hooks/useGetRole";

import { IExsistAppointment } from "@/shared/interfaces/schedulePage";
import { ISearchDrawerProps } from "@/shared/interfaces/ui";

import "./ScheduleTable.scss";
import { adminMessage } from "@/utils/constants";

export default function ScheduleTable({
    toggleDrawer    
}: ISearchDrawerProps) {
    useFetchSchedule();
    const { isAdmin } = useGetRole();
    const timeSlots = generateTimeSlotsFunc();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const schedule = useSelector((state: RootState) => state.schedule.schedule);
    
    const [popoverData, setPopoverData] = useState<{
        anchor: HTMLElement | null;
        appointment: IExsistAppointment | null;
    }>({
        anchor: null,
        appointment: null,
    });
    const globalDate = useSelector(
        (state: RootState) => state.schedule.globalDate,
    );

    const handlePopoverOpen = (
        event: React.MouseEvent<HTMLElement>,
        appointment: IExsistAppointment,
    ) => {
        dispatch(toggleVisibilityCalendarInScheduleHeader(false));
        const foundAppointment =
            schedule.find(item => item._id === appointment._id) || null;
        setPopoverData({
            anchor: event.currentTarget,
            appointment: foundAppointment,
        });
    };

    const handlePopoverClose = () => {
        setPopoverData({ anchor: null, appointment: null });
    };

    const editAppointment = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        const target = e.target as HTMLElement;
        const newArr = schedule.filter(item => item._id === target.id);
        if (newArr[0]?.patientId?._id) {
            dispatch(updateExistAppointment(newArr[0]));
            dispatch(setPatientId(newArr[0].patientId._id));
            dispatch(setAppointemtId(target.id));
            toggleDrawer(true);
            handlePopoverClose();
        } else {
            handlePopoverClose();
            toast.error("Дані про пацієнта або лікаря відсутні");
        }
    };

    const handleCreateAppointmentSlot = (
        chairNumber: number,
        startTime: string,
    ) => {
        if (isAdmin) {
            dispatch(
                updateNewAppointment({
                    chairNumber,
                    startTime: startTime.padStart(5, "0"),
                    date: globalDate,
                }),
            );
            dispatch(toggleVisibilityCalendarInScheduleHeader(false));
            navigate("/appointments");
        } else toast.error(adminMessage);
    };

    return (
        <main className='schedule-container'>
            <aside className='time-column'>
                {timeSlots.map((time: string, index: number) => (
                    <div
                        key={index}
                        className={`time-slot ${
                            time.includes(":00") ? "full-hour" : "half-hour"
                        }`}
                    >
                        {time}
                    </div>
                ))}
            </aside>
            <div className='clinic-columns'>
                {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className='clinic-column'>
                        {timeSlots.map((time: string, index: number) => {
                            if (time === "23:00") return null;

                            const appointment: IExsistAppointment | undefined =
                                schedule.length > 0
                                    ? schedule.find(
                                          appt =>
                                              normalizeTime(appt.startTime) ===
                                                  normalizeTime(time) &&
                                              appt.chairNumber === i + 1,
                                      )
                                    : undefined;
                            return (
                                <React.Fragment key={`${i}-${time}`}>
                                    <div
                                        key={index}
                                        onClick={() =>
                                            handleCreateAppointmentSlot(
                                                i + 1,
                                                time,
                                            )
                                        }
                                        className={`appointment-slot ${
                                            time.includes(":00")
                                                ? "full-hour-line"
                                                : "half-hour-line"
                                        }`}
                                    />
                                    {appointment ? (
                                        <div
                                            key={appointment._id}
                                            id={appointment._id}
                                            className={`appointment isHover ${
                                                appointment.isAppointmentComplete
                                                    ? "closed_appointment"
                                                    : ""
                                            } ${
                                                appointment.hasVisited
                                                    ? ""
                                                    : "hasDontVisited hasDontVisitedappointment"
                                            }`}
                                            onClick={e => {
                                                editAppointment(e);
                                            }}
                                            onMouseEnter={e =>
                                                handlePopoverOpen(
                                                    e,
                                                    appointment,
                                                )
                                            }
                                            onMouseLeave={handlePopoverClose}
                                            style={{
                                                height: `${
                                                    (appointment.duration /
                                                        30) *
                                                    53
                                                }px`,
                                            }}
                                        >
                                            <ScheduleTablePopover
                                                open={Boolean(
                                                    popoverData.anchor &&
                                                        popoverData.appointment
                                                            ?._id ===
                                                            appointment._id,
                                                )}
                                                anchorEl={popoverData.anchor}
                                                handlePopoverClose={
                                                    handlePopoverClose
                                                }
                                                appointment={appointment}
                                            />
                                            <div
                                                className={`appointment_timer ${
                                                    appointment.hasVisited
                                                        ? ""
                                                        : "hasDontVisited_timer"
                                                }`}
                                                id={appointment._id}
                                            >
                                                {appointment.startTime} -{" "}
                                                {normalizeTime(
                                                    calculateEndTime(
                                                        appointment.startTime,
                                                        appointment.duration,
                                                    ),
                                                )}
                                            </div>
                                            <div
                                                className='appointment_patient_name'
                                                id={appointment._id}
                                            >
                                                {appointment.patientId?.name}
                                            </div>
                                            <div
                                                className='appointment_doc_name'
                                                id={appointment._id}
                                            >
                                                Лікар:{" "}
                                                {appointment.doctorId?.name}
                                            </div>
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ))}
            </div>
        </main>
    );
}
